import { memo, useMemo, useState } from 'react';
import {
  Trends as TrendsVisualization,
  type TrendResultT,
} from '@mentimeter/visualizations';
import type { VisualizationThemeT } from '@mentimeter/question-modules-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import type { InteractiveContent } from '@core-api/presentation/types/response';
import { usePresentationContext } from '../../../legacy-context/presentationContext';
import { VisualizationUpdater } from '../../../shared/components/visualizations-legacy/VisualizationUpdater';
import { SvgElement } from '../../../shared/components/visualizations-legacy/SvgElement';
import { getBarColors } from '../../../shared/utils/getColors';
import { useUniqueSelector } from '../../../shared/hooks/useUniqueSelector';
import { getTrendsTestVotes } from '../utils/getTrendsTestVotes';
import type { ScalesRange } from '../types';

type Props = VisualizationThemeT & {
  selector: string;
  results: TrendResultT[];
  choices: InteractiveContent['choices'];
};

const TrendsComponent = memo<Props>(
  ({ selector, results, fillColors, textColor, choices }: Props) => {
    const [hoverId, setHoverId] = useState<null | string>(null);

    const visualizationChoices = choices.map((choice) => ({
      id: choice.interactiveContentChoiceId,
      label: choice.title,
    }));

    const trends = new TrendsVisualization({
      selector: `#${selector}`,
      results,
      colors: getBarColors(fillColors),
      textColor,
      type: 'scales',
      hoverId,
      setHoverId,
      choices: visualizationChoices,
    });

    return (
      <SvgElement>
        <VisualizationUpdater updateFn={trends.update} />
        <g id={selector} data-testid="trends-graph" />
      </SvgElement>
    );
  },
);

interface TrendsDataHandlerProps {
  choices: InteractiveContent['choices'];
  range: ScalesRange;
  shouldHideResults: boolean;
}

export const TrendsDataHandler = ({
  choices,
  range,
  shouldHideResults,
}: TrendsDataHandlerProps) => {
  const { useTrends, isPreviewResultsEnabled } = usePresentationContext();
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const selector = useUniqueSelector();

  const { results } = useTrends();

  const trendsTestResults = useMemo(
    () => getTrendsTestVotes({ choices, range }),
    [choices, range],
  );

  const trendResults = shouldHideResults
    ? []
    : isPreviewResultsEnabled
      ? trendsTestResults
      : results;

  return (
    <TrendsComponent
      selector={selector}
      fillColors={fillColors}
      textColor={textColor}
      results={trendResults}
      choices={choices}
    />
  );
};
